<template>
  <div class="create_company-page-wrapper create-company-page--glass">
    <div class="create_company-wizard-container create-company-glass-shell glass-tab-view glass-tab-view--flush">
      <!-- Add warning modal -->
      <SetupWaring :visible="showCancelWarning" @confirm="confirmCancel" @cancel="showCancelWarning = false" />

      <!-- Stepper Component -->
      <div class="create_company-wizard-nav create-company-stepper-panel glass-tab-panel--nested">
        <Stepper 
          :steps="stepperSteps" 
          :current-step="currentStepIndex"
          :clickable="true"
          @step-click="handleStepperClick"
        />
      </div>

      <div class="create_company-wizard-content glass-tab-view__content create-company-wizard-stack">
        <!-- Company Info Step -->
        <div v-if="currentStep === 'company-info'" class="create_company-step-content glass-tab-panel--nested" :style="stepPanelStyle">
          <CreateCompanyStepHeader title="Company Information" icon="bi bi-building" />
          <form @submit.prevent="nextStep">
            <div class="create_company-form-section">
              <LabelInputField
                input-id="cc-company-name"
                label="Company Name"
                v-model="formData.companyName"
                placeholder="Enter company name"
                required
                flex
                :has-error="!!(errors.companyName || (touched.companyName && !formData.companyName))"
                @blur="onFieldBlur('companyName', validateCompanyInfoStep)"
              >
                <span v-if="errors.companyName || (touched.companyName && !formData.companyName)" class="create_company-field-error">
                  {{ errors.companyName || 'Company name is required' }}
                </span>
              </LabelInputField>
              <LabelInputField
                input-id="cc-legal-name"
                label="Legal Name"
                v-model="formData.legalName"
                placeholder="Enter legal name"
                required
                flex
                :has-error="!!(errors.legalName || (touched.legalName && !formData.legalName))"
                @blur="onFieldBlur('legalName', validateCompanyInfoStep)"
              >
                <span v-if="errors.legalName || (touched.legalName && !formData.legalName)" class="create_company-field-error">
                  {{ errors.legalName || 'Legal name is required' }}
                </span>
              </LabelInputField>
              <LabelInputField
                input-id="cc-federal-id"
                label="Federal ID"
                v-model="formData.federalId"
                placeholder="Enter federal ID"
                required
                flex
                :has-error="!!(errors.federalId || (touched.federalId && !formData.federalId))"
                @blur="onFieldBlur('federalId', validateCompanyInfoStep)"
              >
                <span v-if="errors.federalId || (touched.federalId && !formData.federalId)" class="create_company-field-error">
                  {{ errors.federalId || 'Federal ID is required' }}
                </span>
              </LabelInputField>
            </div>
            <div class="create_company-form-section">
              <LabelInputField
                input-id="cc-phone"
                label="Phone Number"
                v-model="formData.phone"
                type="tel"
                placeholder="Enter phone number"
                required
                flex
                :has-error="!!(errors.phone || (touched.phone && !formData.phone))"
                @blur="onFieldBlur('phone', validateCompanyInfoStep)"
              >
                <span v-if="errors.phone || (touched.phone && !formData.phone)" class="create_company-field-error">
                  {{ errors.phone || 'Phone number is required' }}
                </span>
              </LabelInputField>
              <LabelInputField
                input-id="cc-email"
                label="Email Address"
                v-model="formData.email"
                type="email"
                placeholder="Enter email address"
                flex
                :has-error="!!errors.email"
                @blur="onFieldBlur('email', validateCompanyInfoStep)"
              >
                <span v-if="errors.email" class="create_company-field-error">{{ errors.email }}</span>
              </LabelInputField>
              <LabelInputField
                input-id="cc-website"
                label="Website"
                v-model="formData.website"
                type="url"
                placeholder="Enter website URL"
                required
                flex
                :has-error="!!(errors.website || (touched.website && !formData.website))"
                @blur="onFieldBlur('website', validateCompanyInfoStep)"
              >
                <span v-if="errors.website || (touched.website && !formData.website)" class="create_company-field-error">
                  {{ errors.website || 'Website URL is required' }}
                </span>
              </LabelInputField>
            </div>
            <!-- Country, state, city, postal (shared AddressSelector) + fax + timezone -->
            <div class="create_company-form-section create_company-address-selector-section">
              <AddressSelector
                class="create_company-address-selector"
                :country="formData.country"
                :state="formData.region"
                :city="formData.city"
                :postal-code="formData.zipCode"
                :required="true"
                @update:country="onAddressCountryUpdate($event)"
                @update:state="onAddressStateUpdate($event)"
                @update:city="onAddressCityUpdate($event)"
                @update:postalCode="onAddressPostalUpdate($event)"
              />
              <div v-if="errors.city || (touched.city && !formData.city)" class="invalid-feedback create_company-address-selector-feedback">
                {{ errors.city || 'City is required' }}
              </div>
              <div v-if="errors.zipCode || (touched.zipCode && !formData.zipCode)" class="invalid-feedback create_company-address-selector-feedback">
                {{ errors.zipCode || 'ZIP / postal code is required' }}
              </div>
              <div v-if="errors.country" class="invalid-feedback create_company-address-selector-feedback">
                {{ errors.country }}
              </div>
              <div v-if="errors.region" class="invalid-feedback create_company-address-selector-feedback">
                {{ errors.region }}
              </div>
            </div>
            <div class="create_company-form-section create_company-form-section--two-cols">
              <LabelInputField
                input-id="cc-fax"
                label="Fax"
                v-model="formData.fax"
                type="tel"
                placeholder="Enter fax number"
                flex
              />
              <LabelDropdown
                label="Timezone"
                select-id="cc-timezone"
                v-model="formData.timezone"
                flex
                wide
                searchable
                search-placeholder="Search timezones..."
                :disabled="!formData.country || availableTimezones.length === 0"
                :options="timezoneDropdownOptions"
              />
            </div>
            <div class="create_company-form-section create_company-address-section">
              <LabelDescriptionField
                input-id="cc-address-line-1"
                label="Address Line 1"
                v-model="formData.addressLine1"
                placeholder="Enter street address"
                :rows="2"
                required
                flex
                :has-error="!!(errors.addressLine1 || (touched.addressLine1 && !formData.addressLine1))"
                @blur="onFieldBlur('addressLine1', validateCompanyInfoStep)"
              >
                <span v-if="errors.addressLine1 || (touched.addressLine1 && !formData.addressLine1)" class="create_company-field-error">
                  {{ errors.addressLine1 || 'Address is required' }}
                </span>
              </LabelDescriptionField>
              <LabelDescriptionField
                input-id="cc-address-line-2"
                label="Address Line 2"
                v-model="formData.addressLine2"
                placeholder="Apartment, suite, unit, etc."
                :rows="2"
                flex
              />
            </div>
            <CreateCompanyWizardActions
              :show-back="false"
              primary-label="Next"
              @cancel="cancel"
              @primary="nextStep"
            />
          </form>
        </div>

        <!-- Industry Type Step -->
        <div v-if="currentStep === 'industry-type'" class="create_company-step-content glass-tab-panel--nested" :style="stepPanelStyle">
          <CreateCompanyStepHeader title="Industry & organization" icon="bi bi-briefcase" />
          <div class="create_company-form-section create_company-form-section--stacked">
            <LabelDropdown
              label="Industry Type"
              select-id="cc-industry"
              v-model="formData.industryId"
              flex
              wide
              searchable
              required
              search-placeholder="Search industries..."
              :options="industryDropdownOptions"
              :has-error="!!(errors.industryId || (touched.industryId && !formData.industryId))"
              @change="onFieldBlur('industryId', validateIndustryStep)"
            >
              <span v-if="errors.industryId || (touched.industryId && !formData.industryId)" class="create_company-field-error">
                {{ errors.industryId || 'Industry type is required' }}
              </span>
            </LabelDropdown>
            <LabelDropdown
              label="Organization type (legal entity)"
              select-id="cc-organization-type"
              v-model="formData.organizationType"
              flex
              wide
              searchable
              required
              search-placeholder="Search organization types..."
              :options="organizationTypeDropdownOptions"
              :has-error="!!(errors.organizationType || (touched.organizationType && formData.organizationType === ''))"
              @change="onFieldBlur('organizationType', validateIndustryStep)"
            >
              <span
                v-if="errors.organizationType || (touched.organizationType && formData.organizationType === '')"
                class="create_company-field-error"
              >
                {{ errors.organizationType || 'Organization type is required' }}
              </span>
            </LabelDropdown>
          </div>
          <CreateCompanyWizardActions @back="back" @cancel="cancel" @primary="nextStep" />
        </div>

        <!-- Terminology Step -->
        <div v-if="currentStep === 'terminology'" class="create_company-step-content glass-tab-panel--nested" :style="stepPanelStyle">
          <CreateCompanyStepHeader title="Customize Your Terminology" icon="bi bi-gear" />
          <div class="create_company-terminology-section">
            <div class="create_company-form-section create_company-form-section--two-cols">
              <LabelDropdown
                label="How do you refer to your customers?"
                select-id="cc-customer-term"
                v-model="formData.customerTerm"
                flex
                wide
                :options="customerTermOptions"
              />
              <LabelDropdown
                label="How do you refer to your vendors?"
                select-id="cc-vendor-term"
                v-model="formData.vendorTerm"
                flex
                wide
                :options="vendorTermOptions"
              />
            </div>

            <div class="create_company-row">
              <div class="create_company-col-md-12">
                <div class="create_company-form-group">
                  <label>Do you want to create Quotes in Anna Books?</label>
                  <div class="create_company-radio-group">
                    <label class="create_company-radio-label">
                      <input type="radio" v-model="formData.createQuotes" :value="true" />
                      <span>Yes</span>
                    </label>
                    <label class="create_company-radio-label">
                      <input type="radio" v-model="formData.createQuotes" :value="false" />
                      <span>No</span>
                    </label>
                  </div>
                </div>
              </div>
            </div>

            <div class="create_company-form-section create_company-form-section--two-cols">
              <LabelDropdown
                label="Select Currency"
                select-id="cc-primary-currency"
                v-model="formData.primaryCurrency"
                flex
                wide
                searchable
                search-placeholder="Search currencies..."
                :options="currencyOptions"
              />
              <LabelDropdown
                label="Fiscal Year Start"
                select-id="cc-fiscal-year"
                v-model="formData.fiscalYearStart"
                flex
                wide
                :options="fiscalYearOptions"
              />
            </div>

            <div class="create_company-form-section create_company-form-section--full">
              <LabelCheckboxField
                input-id="cc-enable-multi-currency"
                label="Enable multi-currency"
                v-model="formData.enableMultiCurrency"
                flex
              />
            </div>

            <div class="create_company-cash-flow-section">
              <h3>Cash Flow Management</h3>
              <p>Knowing what money you owe (your accounts payable) is an important part of managing your cash flow. Anna Books can help you manage your cash flow by:</p>
              <ul>
                <li>Knowing which vendors you owe money to</li>
                <li>Tracking how much money you owe</li>
                <li>Reminding you when bills are due</li>
              </ul>
              <div class="create_company-radio-group">
                <label class="create_company-radio-label">
                  <input type="radio" v-model="formData.cashFlowTracking" value="yes" />
                  <span>Yes, help me track this</span>
                </label>
                <label class="create_company-radio-label">
                  <input type="radio" v-model="formData.cashFlowTracking" value="no" />
                  <span>No, I'll handle this another way</span>
                </label>
              </div>
            </div>
          </div>

          <CreateCompanyWizardActions @back="back" @cancel="cancel" @primary="nextStep" />
        </div>

        <!-- Services/Products Step -->
        <div v-if="currentStep === 'services-products'" class="create_company-step-content glass-tab-panel--nested" :style="stepPanelStyle">
          <CreateCompanyStepHeader title="Services & Products Setup" icon="bi bi-cart" />
          <CreateCompanyServicesProductsSetup
            v-model:offer-type="formData.offerType"
            v-model:enable-inventory="formData.enableInventory"
            v-model:multiple-warehouses="formData.multipleWarehouses"
            v-model:enable-units="formData.enableUnits"
            v-model:inventory-scheme="formData.inventoryScheme"
            v-model:enable-tax="formData.enableTax"
            v-model:tax-item-type="formData.taxItemType"
            v-model:track-tax-paid="formData.trackTaxPaid"
          />
          <CreateCompanyWizardActions @back="back" @cancel="cancel" @primary="nextStep" />
        </div>

        <!-- Accounts Step -->
        <div v-if="currentStep === 'accounts'" class="create_company-step-content glass-tab-panel--nested" :style="stepPanelStyle">
          <CreateCompanyStepHeader title="Review Income & Expenses" icon="bi bi-journal-text" />
          <p class="create_company-description">Using statements in Anna Books</p>
          <p class="create_company-note">Note: Recommended accounts for your industry are typically all you need for income tax purposes.</p>
          
          <div class="create_company-accounts-wrapper">
            <div class="create_company-accounts-section">
              <h3>Cost of Goods Sold</h3>
              <div class="create_company-account-list">
                <label class="create_company-account-item">
                  <input type="checkbox" v-model="formData.costOfGoodsSold.inventory" />
                  <i class="bi bi-box"></i>
                  Inventory
                </label>
                <label class="create_company-account-item">
                  <input type="checkbox" v-model="formData.costOfGoodsSold.labor" />
                  <i class="bi bi-person-workspace"></i>
                  Labor
                </label>
                <label class="create_company-account-item">
                  <input type="checkbox" v-model="formData.costOfGoodsSold.materials" />
                  <i class="bi bi-tools"></i>
                  Materials
                </label>
              </div>
            </div>

            <div class="create_company-accounts-section">
              <h3>Expenses</h3>
              <div class="create_company-account-list">
                <label class="create_company-account-item">
                  <input type="checkbox" v-model="formData.expenses.telephone" />
                  <i class="bi bi-telephone"></i>
                  Telephone Expenses
                </label>
                <label class="create_company-account-item">
                  <input type="checkbox" v-model="formData.expenses.travel" />
                  <i class="bi bi-airplane"></i>
                  Travel Expenses
                </label>
                <label class="create_company-account-item">
                  <input type="checkbox" v-model="formData.expenses.utilities" />
                  <i class="bi bi-lightning"></i>
                  Utilities Expenses
                </label>
              </div>
            </div>

            <div class="create_company-accounts-section">
              <h3>Other Income</h3>
              <div class="create_company-account-list">
                <label class="create_company-account-item">
                  <input type="checkbox" v-model="formData.otherIncome.financeCharge" />
                  <i class="bi bi-cash"></i>
                  Finance charge income
                </label>
                <label class="create_company-account-item">
                  <input type="checkbox" v-model="formData.otherIncome.insurance" />
                  <i class="bi bi-shield"></i>
                  Insurance proceeds received
                </label>
                <label class="create_company-account-item">
                  <input type="checkbox" v-model="formData.otherIncome.interest" />
                  <i class="bi bi-bank"></i>
                  Interest Income
                </label>
                <label class="create_company-account-item">
                  <input type="checkbox" v-model="formData.otherIncome.assetSales" />
                  <i class="bi bi-cart"></i>
                  Proceeds from sales of Assets
                </label>
              </div>
            </div>
          </div>

          <div class="create_company-account-actions create_company-wizard-actions create_company-account-actions--inline">
            <SecondaryButton
              label="Restore Recommendations"
              icon="bi bi-arrow-counterclockwise"
              custom-class="create_company-wizard-btn create_company-btn-link-style"
              @click="restoreRecommendations"
            />
            <SecondaryButton
              label="Select All"
              icon="bi bi-check2-all"
              custom-class="create_company-wizard-btn create_company-btn-link-style"
              @click="selectAll"
            />
          </div>

          <div class="create_company-info-box">
            <h4>Why should I use the recommended accounts?</h4>
            <p>You can modify your chart of accounts any time after completing this interview. The recommended accounts are designed to give you a solid foundation for financial tracking and reporting.</p>
          </div>

          <CreateCompanyWizardActions @back="back" @cancel="cancel" @primary="nextStep" />
        </div>

        <!-- Bill Tracking Step -->
        <div v-if="currentStep === 'bill-tracking'" class="create_company-step-content glass-tab-panel--nested" :style="stepPanelStyle">
          <CreateCompanyStepHeader title="Managing Bills you owe?" icon="bi bi-receipt" />
          <div style="margin-bottom: 1em;">
            <p>Knowing what money you owe--Your accounts payable--is an important part of managing your cash flow.<br/>
            Anna Books can help you manage your cash flow by:<br/>
            Knowing which vendors you owe money to<br/>
            tracking how much money you owe<br/>
            Reminding you when bills are due</p>
          </div>
          <div class="create_company-form-group">
            <label>Do you want to track bills you owe?</label>
            <div class="create_company-radio-group">
              <label class="create_company-radio-label">
                <input type="radio" v-model="formData.billTracking" :value="true" />
                <span>Yes</span>
              </label>
              <label class="create_company-radio-label">
                <input type="radio" v-model="formData.billTracking" :value="false" />
                <span>No</span>
              </label>
            </div>
          </div>
          <CreateCompanyWizardActions @back="back" @cancel="cancel" @primary="nextStep" />
        </div>

        <!-- Encryption Step -->
        <div v-if="currentStep === 'encryption'" class="create_company-step-content glass-tab-panel--nested" :style="stepPanelStyle">
          <CreateCompanyStepHeader title="Setup Encryption" icon="bi bi-shield-lock" />
          <div class="create_company-encryption-section">
            <LabelCheckboxField
              input-id="cc-enable-encryption"
              label="Encryption enables your company data to be encrypted on the server in such way that no one will be able to read your data without this password."
              v-model="formData.enableEncryption"
              flex
            />
            <p class="create_company-encryption-note">
              Please note that all financial numbers will not be encrypted as they are needed for proper operation of the accounting software.
            </p>
            <p class="create_company-encryption-example">
              Example: data that is encrypted: customer names, vendor names, account names, address, phone numbers, transaction numbers, memos, notes, etc.
            </p>
            
            <div v-if="formData.enableEncryption" class="create_company-encryption-form create_company-form-section create_company-form-section--stacked">
              <LabelInputField
                input-id="cc-encryption-password"
                label="Company Password"
                v-model="formData.encryptionPassword"
                type="password"
                placeholder="Enter password"
                required
                flex
                :has-error="!!(errors.encryptionPassword || (touched.encryptionPassword && !formData.encryptionPassword && formData.enableEncryption))"
                @blur="onEncryptionFieldBlur"
                @input="validateEncryptionPasswordMatchRealTime"
              >
                <span v-if="errors.encryptionPassword || (touched.encryptionPassword && !formData.encryptionPassword && formData.enableEncryption)" class="create_company-field-error">
                  {{ errors.encryptionPassword || 'Company password is required' }}
                </span>
              </LabelInputField>
              <LabelInputField
                input-id="cc-encryption-password-confirm"
                label="Confirm Password"
                v-model="formData.encryptionPasswordConfirm"
                type="password"
                placeholder="Confirm password"
                required
                flex
                :has-error="!!(errors.encryptionPasswordConfirm || (touched.encryptionPasswordConfirm && !formData.encryptionPasswordConfirm && formData.enableEncryption))"
                @blur="onEncryptionFieldBlur"
                @input="validateEncryptionPasswordMatchRealTime"
              >
                <span v-if="errors.encryptionPasswordConfirm || (touched.encryptionPasswordConfirm && !formData.encryptionPasswordConfirm && formData.enableEncryption)" class="create_company-field-error">
                  {{ errors.encryptionPasswordConfirm || 'Please confirm your password' }}
                </span>
              </LabelInputField>
              <LabelInputField
                input-id="cc-password-hint"
                label="Password Hint"
                v-model="formData.passwordHint"
                placeholder="Enter password hint"
                flex
              />
              <div class="create_company-warning-box">
                <p>This password is mandatory without this you will not be able to open the company. Losing this password will render all your data useless. You can change this password after opening a company. You will be asked to enter this password while opening your company first time and when you change your account password.</p>
              </div>
            </div>
          </div>
          <CreateCompanyWizardActions @back="back" @cancel="cancel" @primary="nextStep" />
        </div>

        <!-- Congratulations Step -->
        <div v-if="currentStep === 'congratulations'" class="create_company-step-content glass-tab-panel--nested" :style="stepPanelStyle">
          <CreateCompanyStepHeader title="Congratulations!" icon="bi bi-check-circle" />
          <div class="create_company-congratulations-content">
            <p>Your company has been successfully set up. You can now start using Anna Books.</p>
            <PrimaryButton
              label="Go to Dashboard"
              icon="bi bi-box-arrow-in-right"
              custom-class="create_company-wizard-btn"
              @click="finish"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import './CreateCompany.css';
import './create-company-glass.css';
import CompanyService from '../services/company.service';
import logger from '@/utils/logger';
import AddressSelector from '@/components/AddressSelector.vue';
import SetupWaring from './SetupWaring.vue';
import Stepper from '../components/Stepper.vue';
import CreateCompanyStepHeader from './components/CreateCompanyStepHeader.vue';
import CreateCompanyWizardActions from './components/CreateCompanyWizardActions.vue';
import CreateCompanyServicesProductsSetup from './components/CreateCompanyServicesProductsSetup.vue';
import LabelInputField from '@/modules/consultant/components/LabelInputField.vue';
import LabelDropdown from '@/modules/consultant/components/LabelDropdown.vue';
import LabelDescriptionField from '@/modules/consultant/components/LabelDescriptionField.vue';
import LabelCheckboxField from '@/modules/consultant/components/LabelCheckboxField.vue';
import PrimaryButton from '@/modules/consultant/components/PrimaryButton.vue';
import {
  CREATE_COMPANY_CURRENCY_OPTIONS,
  CREATE_COMPANY_CUSTOMER_TERM_OPTIONS,
  CREATE_COMPANY_VENDOR_TERM_OPTIONS,
  CREATE_COMPANY_FISCAL_YEAR_OPTIONS,
} from '@/constants/createCompanyFormOptions';
import { getTimezoneByLocation } from '@/utils/timezoneMapping';
import {
  countryIsoCodeToEnglishName,
  regionCodeToEnglishName,
  IANA_TO_COMPANY_TIMEZONE_LABEL,
} from '@/utils/addressFormatForCompanyApi';
import { ORGANIZATION_TYPES_FOR_SELECT } from '@/constants/companyOrganizationTypes';
import { INDUSTRY_TYPES_FOR_SELECT } from '@/constants/companyIndustryTypes';
import {
  companyValidateCompanyName,
  companyValidateLegalName,
  companyValidateFederalId,
  companyValidatePhone,
  companyValidateEmail,
  companyValidateWebsiteUrl,
  companyValidateAddress,
  companyValidateCity,
  companyValidateZipCode,
  companyValidateEncryptionPassword,
  companyValidateEncryptionPasswordMatch,
  companyValidateIndustry,
  companyValidateOrganizationType
} from '../utils/companyValidation';

export default {
  name: 'CreateCompany',
  components: {
    AddressSelector,
    SetupWaring,
    Stepper,
    CreateCompanyStepHeader,
    CreateCompanyWizardActions,
    CreateCompanyServicesProductsSetup,
    LabelInputField,
    LabelDropdown,
    LabelDescriptionField,
    LabelCheckboxField,
    PrimaryButton,
  },
  props: {
    skipSetup: {
      type: Boolean,
      default: false
    }
  },
  emits: ['company-created', 'cancel'],
  data() {
    return {
      isLoading: false,
      currentStep: 'company-info',
      isSkipSetup: false,
      industrySearch: '',
      showCancelWarning: false,
      errors: {},
      touched: {
        companyName: false,
        legalName: false,
        federalId: false,
        phone: false,
        email: false,
        website: false,
        addressLine1: false,
        city: false,
        zipCode: false,
        industryId: false,
        organizationType: false,
        encryptionPassword: false,
        encryptionPasswordConfirm: false
      },
      formData: {
        // Company Info
        companyName: '',
        legalName: '',
        federalId: '',
        country: '',
        region: '',
        addressLine1: '',
        addressLine2: '',
        city: '',
        zipCode: '',
        phone: '',
        fax: '',
        email: '',
        website: '',
        timezone: '',

        // Industry Type
        industryId: '',
        organizationType: '',

        // Terminology
        customerTerm: 'Customer',
        vendorTerm: 'Vendor',
        createQuotes: false,
        primaryCurrency: 'USD',
        enableMultiCurrency: false,
        fiscalYearStart: 'January',
        cashFlowTracking: 'no',

        // Services/Products
        offerType: '',
        enableInventory: false,
        multipleWarehouses: false,
        enableUnits: false,
        inventoryScheme: 'average',
        enableTax: false,
        taxItemType: 'transaction',
        trackTaxPaid: false,

        // Accounts
        costOfGoodsSold: {
          inventory: false,
          labor: false,
          materials: false
        },
        expenses: {
          telephone: false,
          travel: false,
          utilities: false
        },
        otherIncome: {
          financeCharge: false,
          insurance: false,
          interest: false,
          assetSales: false
        },

        // Encryption
        enableEncryption: false,
        encryptionPassword: '',
        encryptionPasswordConfirm: '',
        passwordHint: '',

        // Currency
        currencyId: 1, // Default to USD
        billTracking: true,
      },
      industries: INDUSTRY_TYPES_FOR_SELECT,

      timezones: [
        'UTC-12:00 Baker Island',
        'UTC-11:00 American Samoa, Niue',
        'UTC-10:00 Hawaii, Cook Islands',
        'UTC-09:30 Marquesas Islands',
        'UTC-09:00 Alaska, French Polynesia',
        'UTC-08:00 Pacific Time (US & Canada)',
        'UTC-07:00 Mountain Time (US & Canada)',
        'UTC-06:00 Central Time (US & Canada), Mexico City',
        'UTC-05:00 Eastern Time (US & Canada), Bogota, Lima',
        'UTC-04:00 Atlantic Time (Canada), Caracas, La Paz',
        'UTC-03:30 Newfoundland',
        'UTC-03:00 Brazil, Buenos Aires, Montevideo',
        'UTC-02:00 Mid-Atlantic',
        'UTC-01:00 Azores, Cape Verde Islands',
        'UTC+00:00 London, Dublin, Lisbon',
        'UTC+01:00 Berlin, Paris, Rome, Madrid',
        'UTC+02:00 Helsinki, Kiev, Cairo, Cape Town',
        'UTC+03:00 Moscow, Baghdad, Kuwait, Nairobi',
        'UTC+03:30 Tehran',
        'UTC+04:00 Abu Dhabi, Muscat, Baku',
        'UTC+04:30 Kabul',
        'UTC+05:00 Islamabad, Karachi, Tashkent',
        'UTC+05:30 Mumbai, Colombo',
        'UTC+05:30 Kolkata, India',
        'UTC+05:45 Kathmandu',
        'UTC+06:00 Dhaka, Almaty, Novosibirsk',
        'UTC+06:30 Yangon (Rangoon)',
        'UTC+07:00 Bangkok, Hanoi, Jakarta',
        'UTC+08:00 Beijing, Hong Kong, Singapore',
        'UTC+08:45 Eucla',
        'UTC+09:00 Tokyo, Seoul, Osaka',
        'UTC+09:30 Adelaide, Darwin',
        'UTC+10:00 Sydney, Melbourne, Brisbane',
        'UTC+10:30 Lord Howe Island',
        'UTC+11:00 Noumea, Solomon Islands',
        'UTC+12:00 Auckland, Wellington, Fiji',
        'UTC+12:45 Chatham Islands',
        'UTC+13:00 Samoa, Tonga',
        'UTC+14:00 Line Islands'
      ],
      timezonesByCountry: {
        'United States': [
          'UTC-10:00 Hawaii, Cook Islands',
          'UTC-09:00 Alaska, French Polynesia',
          'UTC-08:00 Pacific Time (US & Canada)',
          'UTC-07:00 Mountain Time (US & Canada)',
          'UTC-06:00 Central Time (US & Canada), Mexico City',
          'UTC-05:00 Eastern Time (US & Canada), Bogota, Lima'
        ],
        'United Kingdom': [
          'UTC+00:00 London, Dublin, Lisbon'
        ],
        'Canada': [
          'UTC-08:00 Pacific Time (US & Canada)',
          'UTC-07:00 Mountain Time (US & Canada)',
          'UTC-06:00 Central Time (US & Canada), Mexico City',
          'UTC-05:00 Eastern Time (US & Canada), Bogota, Lima',
          'UTC-04:00 Atlantic Time (Canada), Caracas, La Paz',
          'UTC-03:30 Newfoundland'
        ],
        'Australia': [
          'UTC+08:00 Beijing, Hong Kong, Singapore',
          'UTC+08:45 Eucla',
          'UTC+09:30 Adelaide, Darwin',
          'UTC+10:00 Sydney, Melbourne, Brisbane',
          'UTC+10:30 Lord Howe Island'
        ],
        'India': [
          'UTC+05:30 Kolkata, India'
        ],
        'Mexico': [
          'UTC-08:00 Pacific Time (US & Canada)',
          'UTC-07:00 Mountain Time (US & Canada)',
          'UTC-06:00 Central Time (US & Canada), Mexico City'
        ],
        'Brazil': [
          'UTC-05:00 Eastern Time (US & Canada), Bogota, Lima',
          'UTC-04:00 Atlantic Time (Canada), Caracas, La Paz',
          'UTC-03:00 Brazil, Buenos Aires, Montevideo'
        ],
        'Argentina': [
          'UTC-03:00 Brazil, Buenos Aires, Montevideo'
        ],
        'China': [
          'UTC+08:00 Beijing, Hong Kong, Singapore'
        ],
        'Japan': [
          'UTC+09:00 Tokyo, Seoul, Osaka'
        ],
        'Germany': [
          'UTC+01:00 Berlin, Paris, Rome, Madrid'
        ],
        'France': [
          'UTC+01:00 Berlin, Paris, Rome, Madrid'
        ],
        'Italy': [
          'UTC+01:00 Berlin, Paris, Rome, Madrid'
        ],
        'Spain': [
          'UTC+01:00 Berlin, Paris, Rome, Madrid'
        ],
        'Afghanistan': [
          'UTC+04:30 Kabul'
        ],
        'Bangladesh': [
          'UTC+06:00 Dhaka, Almaty, Novosibirsk'
        ],
        'Egypt': [
          'UTC+02:00 Helsinki, Kiev, Cairo, Cape Town'
        ],
        'Indonesia': [
          'UTC+07:00 Bangkok, Hanoi, Jakarta',
          'UTC+08:00 Beijing, Hong Kong, Singapore',
          'UTC+09:00 Tokyo, Seoul, Osaka'
        ],
        'Iran': [
          'UTC+03:30 Tehran'
        ],
        'Iraq': [
          'UTC+03:00 Moscow, Baghdad, Kuwait, Nairobi'
        ],
        'Ireland': [
          'UTC+00:00 London, Dublin, Lisbon'
        ],
        'Israel': [
          'UTC+02:00 Helsinki, Kiev, Cairo, Cape Town'
        ],
        'Nepal': [
          'UTC+05:45 Kathmandu'
        ],
        'New Zealand': [
          'UTC+12:00 Auckland, Wellington, Fiji',
          'UTC+12:45 Chatham Islands'
        ],
        'Pakistan': [
          'UTC+05:00 Islamabad, Karachi, Tashkent'
        ],
        'Philippines': [
          'UTC+08:00 Beijing, Hong Kong, Singapore'
        ],
        'Russia': [
          'UTC+03:00 Moscow, Baghdad, Kuwait, Nairobi',
          'UTC+04:00 Abu Dhabi, Muscat, Baku',
          'UTC+05:00 Islamabad, Karachi, Tashkent',
          'UTC+06:00 Dhaka, Almaty, Novosibirsk',
          'UTC+07:00 Bangkok, Hanoi, Jakarta',
          'UTC+08:00 Beijing, Hong Kong, Singapore',
          'UTC+09:00 Tokyo, Seoul, Osaka',
          'UTC+10:00 Sydney, Melbourne, Brisbane',
          'UTC+11:00 Noumea, Solomon Islands',
          'UTC+12:00 Auckland, Wellington, Fiji'
        ],
        'Saudi Arabia': [
          'UTC+03:00 Moscow, Baghdad, Kuwait, Nairobi'
        ],
        'South Africa': [
          'UTC+02:00 Helsinki, Kiev, Cairo, Cape Town'
        ],
        'South Korea': [
          'UTC+09:00 Tokyo, Seoul, Osaka'
        ],
        'Thailand': [
          'UTC+07:00 Bangkok, Hanoi, Jakarta'
        ],
        'Turkey': [
          'UTC+03:00 Moscow, Baghdad, Kuwait, Nairobi'
        ],
        'United Arab Emirates': [
          'UTC+04:00 Abu Dhabi, Muscat, Baku'
        ],
        'Vietnam': [
          'UTC+07:00 Bangkok, Hanoi, Jakarta'
        ],
        'Nigeria': [
          'UTC+01:00 Berlin, Paris, Rome, Madrid'
        ],
        'Kenya': [
          'UTC+03:00 Moscow, Baghdad, Kuwait, Nairobi'
        ],
        'Singapore': [
          'UTC+08:00 Beijing, Hong Kong, Singapore'
        ],
        'Malaysia': [
          'UTC+08:00 Beijing, Hong Kong, Singapore'
        ],
        'Taiwan': [
          'UTC+08:00 Beijing, Hong Kong, Singapore'
        ],
        'Hong Kong': [
          'UTC+08:00 Beijing, Hong Kong, Singapore'
        ],
        'Poland': [
          'UTC+01:00 Berlin, Paris, Rome, Madrid'
        ],
        'Netherlands': [
          'UTC+01:00 Berlin, Paris, Rome, Madrid'
        ],
        'Belgium': [
          'UTC+01:00 Berlin, Paris, Rome, Madrid'
        ],
        'Switzerland': [
          'UTC+01:00 Berlin, Paris, Rome, Madrid'
        ],
        'Austria': [
          'UTC+01:00 Berlin, Paris, Rome, Madrid'
        ],
        'Portugal': [
          'UTC+00:00 London, Dublin, Lisbon'
        ],
        'Greece': [
          'UTC+02:00 Helsinki, Kiev, Cairo, Cape Town'
        ],
        'Chile': [
          'UTC-03:00 Brazil, Buenos Aires, Montevideo',
          'UTC-04:00 Atlantic Time (Canada), Caracas, La Paz'
        ],
        'Colombia': [
          'UTC-05:00 Eastern Time (US & Canada), Bogota, Lima'
        ],
        'Peru': [
          'UTC-05:00 Eastern Time (US & Canada), Bogota, Lima'
        ],
        'Venezuela': [
          'UTC-04:00 Atlantic Time (Canada), Caracas, La Paz'
        ]
      },
      availableTimezones: [],
      organizationTypeOptions: ORGANIZATION_TYPES_FOR_SELECT,
      customerTermOptions: CREATE_COMPANY_CUSTOMER_TERM_OPTIONS,
      vendorTermOptions: CREATE_COMPANY_VENDOR_TERM_OPTIONS,
      currencyOptions: CREATE_COMPANY_CURRENCY_OPTIONS,
      fiscalYearOptions: CREATE_COMPANY_FISCAL_YEAR_OPTIONS,
      currencies: [
        { id: 1, code: 'USD', name: 'US Dollar', symbol: '$' },
        { id: 2, code: 'GBP', name: 'British Pound', symbol: '£' },
        { id: 3, code: 'EUR', name: 'Euro', symbol: '€' },
        { id: 4, code: 'JPY', name: 'Japanese Yen', symbol: '¥' },
        { id: 5, code: 'INR', name: 'Indian Rupee', symbol: '₹' }
      ],
      completedSteps: new Set(),
    }
  },
  created() {
    // Check if we're in skip setup mode (from prop or route query)
    this.isSkipSetup = this.skipSetup || this.$route.query.skipSetup === 'true'
    if (this.isSkipSetup) {
      // Pre-fill some default values for skip setup
      this.formData = {
        ...this.formData,
        customerTerm: 'Customer',
        vendorTerm: 'Vendor',
        createQuotes: false,
        primaryCurrency: 'USD',
        enableMultiCurrency: false,
        fiscalYearStart: 'January',
        cashFlowTracking: 'no',
        offerType: 'both',
        enableInventory: false,
        enableTax: false,
        costOfGoodsSold: {
          inventory: true,
          labor: true,
          materials: true
        },
        expenses: {
          telephone: true,
          travel: true,
          utilities: true
        },
        otherIncome: {
          financeCharge: true,
          insurance: true,
          interest: true,
          assetSales: true
        },
        billTracking: true,
      }
    }
    this.updateAvailableTimezones()
    this.$nextTick(() => {
      this.applySuggestedTimezoneFromLocation()
    })
  },
  computed: {
    stepPanelStyle() {
      const index = Math.max(0, this.currentStepIndex || 0);
      return { '--cc-step-delay': `${0.06 + index * 0.02}s` };
    },
    industryDropdownOptions() {
      return [
        { value: '', label: 'Select your industry', disabled: true },
        ...this.industries.map((industry) => ({
          value: industry.id,
          label: industry.label,
        })),
      ];
    },
    organizationTypeDropdownOptions() {
      return [
        { value: '', label: 'Select how your business is organized', disabled: true },
        ...this.organizationTypeOptions,
      ];
    },
    timezoneDropdownOptions() {
      if (!this.formData.country || !(this.availableTimezones || []).length) {
        const label = !this.formData.country ? 'Select Country First' : 'No timezones available';
        return [{ value: '', label, disabled: true }];
      }
      return [
        { value: '', label: 'Select Timezone', disabled: true },
        ...(this.availableTimezones || []).map((tz) => ({ value: tz, label: tz })),
      ];
    },
    filteredIndustries() {
      return this.industries.filter(industry =>
        industry.label.toLowerCase().includes(this.industrySearch.toLowerCase())
      )
    },
    // Get all step names in order
    stepNames() {
      return this.isSkipSetup 
        ? ['company-info', 'industry-type', 'encryption', 'congratulations']
        : ['company-info', 'industry-type', 'terminology', 'services-products', 'accounts', 'bill-tracking', 'encryption', 'congratulations'];
    },
    // Generate steps array for Stepper component
    stepperSteps() {
      const stepLabels = this.isSkipSetup
        ? [
            { title: 'Company Info' },
            { title: 'Industry & Org.' },
            { title: 'Encryption' },
            { title: 'Finish' }
          ]
        : [
            { title: 'Company Info' },
            { title: 'Industry & Org.' },
            { title: 'Terminology' },
            { title: 'Services/Products' },
            { title: 'Accounts' },
            { title: 'Bill Tracking' },
            { title: 'Encryption' },
            { title: 'Finish' }
          ];
      return stepLabels;
    },
    // Convert currentStep string to numeric index for Stepper component
    currentStepIndex() {
      return this.stepNames.indexOf(this.currentStep);
    }
  },
  methods: {
    onFieldBlur(fieldKey, validator) {
      if (fieldKey && this.touched[fieldKey] !== undefined) {
        this.touched[fieldKey] = true;
      }
      if (typeof validator === 'function') {
        validator();
      }
    },
    onEncryptionFieldBlur() {
      this.touched.encryptionPassword = true;
      this.touched.encryptionPasswordConfirm = true;
      this.validateEncryptionPasswordMatchRealTime();
    },
    validateCompanyInfoStep() {
      this.errors = {};
      let isValid = true;

      // Validate company name
      const companyNameValidation = companyValidateCompanyName(this.formData.companyName);
      if (!companyNameValidation.isValid) {
        this.errors.companyName = companyNameValidation.error;
        isValid = false;
      }

      // Validate legal name
      const legalNameValidation = companyValidateLegalName(this.formData.legalName);
      if (!legalNameValidation.isValid) {
        this.errors.legalName = legalNameValidation.error;
        isValid = false;
      }

      // Validate federal ID
      const federalIdValidation = companyValidateFederalId(this.formData.federalId);
      if (!federalIdValidation.isValid) {
        this.errors.federalId = federalIdValidation.error;
        isValid = false;
      }

      // Validate phone
      const phoneValidation = companyValidatePhone(this.formData.phone);
      if (!phoneValidation.isValid) {
        this.errors.phone = phoneValidation.error;
        isValid = false;
      }

      // Validate email (optional but must be valid if provided)
      const emailValidation = companyValidateEmail(this.formData.email);
      if (!emailValidation.isValid) {
        this.errors.email = emailValidation.error;
        isValid = false;
      } else {
        // Clear email error if valid or empty
        delete this.errors.email;
      }

      // Validate website (required field)
      const websiteValidation = companyValidateWebsiteUrl(this.formData.website);
      if (!websiteValidation.isValid) {
        this.errors.website = websiteValidation.error;
        isValid = false;
      } else {
        // Clear website error if valid
        delete this.errors.website;
      }

      if (!this.formData.country || String(this.formData.country).trim() === '') {
        this.errors.country = 'Country is required';
        isValid = false;
      }
      if (!this.formData.region || String(this.formData.region).trim() === '') {
        this.errors.region = 'State / region is required';
        isValid = false;
      }

      // Validate address
      const addressValidation = companyValidateAddress(this.formData.addressLine1);
      if (!addressValidation.isValid) {
        this.errors.addressLine1 = addressValidation.error;
        isValid = false;
      }

      // Validate city
      const cityValidation = companyValidateCity(this.formData.city);
      if (!cityValidation.isValid) {
        this.errors.city = cityValidation.error;
        isValid = false;
      }

      // Validate zip code
      const zipCodeValidation = companyValidateZipCode(this.formData.zipCode);
      if (!zipCodeValidation.isValid) {
        this.errors.zipCode = zipCodeValidation.error;
        isValid = false;
      }

      return isValid;
    },
    validateIndustryStep() {
      this.errors = {};
      let isValid = true;
      const industryValidation = companyValidateIndustry(this.formData.industryId);
      if (!industryValidation.isValid) {
        this.errors.industryId = industryValidation.error;
        isValid = false;
      }
      const orgValidation = companyValidateOrganizationType(this.formData.organizationType);
      if (!orgValidation.isValid) {
        this.errors.organizationType = orgValidation.error;
        isValid = false;
      }
      return isValid;
    },
    validateEncryptionStep() {
      if (!this.formData.enableEncryption) {
        return true; // Encryption is optional
      }

      this.errors = {};
      let isValid = true;

      // Validate encryption password
      const passwordValidation = companyValidateEncryptionPassword(this.formData.encryptionPassword);
      if (!passwordValidation.isValid) {
        this.errors.encryptionPassword = passwordValidation.error;
        isValid = false;
      } else {
        // Password is valid, now check match
        const passwordMatchValidation = companyValidateEncryptionPasswordMatch(
          this.formData.encryptionPassword,
          this.formData.encryptionPasswordConfirm
        );
        if (!passwordMatchValidation.isValid) {
          this.errors.encryptionPasswordConfirm = passwordMatchValidation.error;
          isValid = false;
        }
      }

      return isValid;
    },
    validateEncryptionPasswordMatchRealTime() {
      // Only validate if encryption is enabled
      if (!this.formData.enableEncryption) {
        return;
      }

      // Mark fields as touched when user starts typing
      if (this.formData.encryptionPassword || this.formData.encryptionPasswordConfirm) {
        this.touched.encryptionPassword = true;
        this.touched.encryptionPasswordConfirm = true;
      }

      // Validate password
      if (this.touched.encryptionPassword) {
        const passwordValidation = companyValidateEncryptionPassword(this.formData.encryptionPassword);
        if (!passwordValidation.isValid) {
          this.errors.encryptionPassword = passwordValidation.error;
        } else {
          // Clear password error if valid
          delete this.errors.encryptionPassword;
        }
      }

      // Validate password match
      if (this.touched.encryptionPasswordConfirm && this.formData.encryptionPassword) {
        const passwordMatchValidation = companyValidateEncryptionPasswordMatch(
          this.formData.encryptionPassword,
          this.formData.encryptionPasswordConfirm
        );
        if (!passwordMatchValidation.isValid) {
          this.errors.encryptionPasswordConfirm = passwordMatchValidation.error;
        } else {
          // Passwords match, clear error
          delete this.errors.encryptionPasswordConfirm;
        }
      }
    },
    nextStep() {
      // Validate current step before proceeding
      let isValid = true;
      
      if (this.currentStep === 'company-info') {
        isValid = this.validateCompanyInfoStep();
      } else if (this.currentStep === 'industry-type') {
        isValid = this.validateIndustryStep();
      } else if (this.currentStep === 'encryption') {
        isValid = this.validateEncryptionStep();
      }

      if (!isValid) {
        return; // Don't proceed if validation fails
      }

      // Mark current step as completed
      this.completedSteps.add(this.currentStep);
      
      const currentIndex = this.stepNames.indexOf(this.currentStep);
      if (currentIndex < this.stepNames.length - 1) {
        this.currentStep = this.stepNames[currentIndex + 1];
      }
    },
    back() {
      const currentIndex = this.stepNames.indexOf(this.currentStep);
      if (currentIndex > 0) {
        this.currentStep = this.stepNames[currentIndex - 1];
      }
    },
    cancel() {
      this.showCancelWarning = true
    },
    confirmCancel() {
      this.showCancelWarning = false
      this.$emit('cancel')
      // Also try router push as fallback
      if (this.$route.path !== '/home') {
        this.$router.push('/home')
      }
    },
    async finish() {
      try {
        // Show loading state
        this.isLoading = true;

        // Create company using the service
        const result = await CompanyService.createCompany(this.formData);
        logger.log('Company created successfully:', result);

        // Show success message
        this.$toast.success('Company created successfully');

        // Emit event for parent component
        this.$emit('company-created', result);

        // Redirect to dashboard (fallback if not used as component)
        if (this.$route.path === '/company-setup' || this.$route.path === '/welcome-company') {
          this.$router.push('/dashboard');
        }
      } catch (error) {
        // Show error message
        logger.error('Error creating company:', error);
        
        // Handle validation errors
        if (error.message.includes('\n')) {
          // Multiple validation errors
          const errors = error.message.split('\n');
          errors.forEach(err => {
            this.$toast.error(err);
          });
        } else {
          // Single error message
          this.$toast.error(error.message || 'Failed to create company');
        }
      } finally {
        // Hide loading state
        this.isLoading = false;
      }
    },
    selectIndustry(industry) {
      this.formData.industryId = industry.id
    },
    showHelp() {
      // Implement help functionality
    },
    handleLogout() {
      // Implement logout functionality
    },
    restoreRecommendations() {
      // Implement restore recommendations functionality
    },
    selectAll() {
      // Implement select all functionality
    },
    onAddressCountryUpdate(value) {
      this.formData.country = value
      this.onAddressFieldsChanged()
    },
    onAddressStateUpdate(value) {
      this.formData.region = value
      this.onAddressFieldsChanged()
    },
    onAddressCityUpdate(value) {
      this.formData.city = value
      this.validateCompanyInfoStep()
    },
    onAddressPostalUpdate(value) {
      this.formData.zipCode = value
      this.validateCompanyInfoStep()
    },
    onAddressFieldsChanged() {
      this.updateAvailableTimezones()
      this.$nextTick(() => {
        this.applySuggestedTimezoneFromLocation()
      })
    },
    updateAvailableTimezones() {
      if (!this.formData.country || String(this.formData.country).trim() === '') {
        this.availableTimezones = []
        return
      }
      const countryName = countryIsoCodeToEnglishName(this.formData.country)
      if (countryName && this.timezonesByCountry[countryName]) {
        this.availableTimezones = this.timezonesByCountry[countryName]
        if (this.availableTimezones.length > 0 && !this.availableTimezones.includes(this.formData.timezone)) {
          this.formData.timezone = this.availableTimezones[0]
        } else if (this.availableTimezones.length === 0) {
          this.formData.timezone = ''
        }
      } else {
        this.availableTimezones = this.timezones
        if (this.availableTimezones.length > 0 && !this.availableTimezones.includes(this.formData.timezone)) {
          this.formData.timezone = this.availableTimezones[0]
        }
      }
    },
    applySuggestedTimezoneFromLocation() {
      const countryName = countryIsoCodeToEnglishName(this.formData.country)
      const regionName = regionCodeToEnglishName(this.formData.country, this.formData.region)
      if (!countryName || !regionName) {
        return
      }
      const iana = getTimezoneByLocation(countryName, regionName)
      if (!iana) {
        return
      }
      const label = IANA_TO_COMPANY_TIMEZONE_LABEL[iana]
      if (label && this.availableTimezones.includes(label)) {
        this.formData.timezone = label
      }
    },
    async handleSubmit() {
      try {
        this.isLoading = true;
        this.error = null;

        // Create company
        const { data } = await CompanyService.createCompany(this.formData);
        
        // Show success message
        this.$toast.success('Company created successfully!');
        
        // Store company data in Vuex store if needed
        if (data) {
          this.$store.commit('SET_CURRENT_COMPANY', data);
        }
        
        // Redirect to dashboard or next step
        this.$router.push('/dashboard');
      } catch (error) {
        logger.error('Error creating company:', error);
        this.error = error.message;
        
        // Show error in a more user-friendly way
        if (error.message.includes('Please log in')) {
          this.$toast.error('Please log in to continue');
          this.$router.push('/login');
        } else if (error.message.includes('\n')) {
          // Multiple validation errors
          const errors = error.message.split('\n');
          errors.forEach(err => {
            this.$toast.error(err);
          });
        } else if (error.message.includes('Server error')) {
          this.$toast.error('Unable to create company. Please try again later.');
        } else if (error.message.includes('No response')) {
          this.$toast.error('Unable to connect to the server. Please check your internet connection.');
        } else {
          this.$toast.error(error.message);
        }
      } finally {
        this.isLoading = false;
      }
    },
    isStepCompleted(step) {
      return this.completedSteps.has(step);
    },
    async completeSetup() {
        try {
            // Save company data to store or API
            await this.$store.dispatch('saveCompanyData', this.formData)
            // Redirect to dashboard
            this.$router.push('/dashboard')
        } catch (error) {
            logger.error('Error completing setup:', error)
            this.$toast.error('Failed to complete setup')
        }
    },
    switchStep(step) {
      // Allow switching to any step regardless of completion
      if (this.stepNames.includes(step)) {
        this.currentStep = step;
      }
    },
    // Handle stepper click event
    handleStepperClick(stepIndex) {
      if (stepIndex >= 0 && stepIndex < this.stepNames.length) {
        this.currentStep = this.stepNames[stepIndex];
      }
    },
  }
}
</script> 